<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { periodKeyToTaxYearTitle } from '@/helpers/periodDisplay'
import {
  donationsForTaxYearPeriod,
  isDonorAddedDonation,
} from '@/helpers/taxPackDonations'
import StatementPeriodHeader from '@/components/dashboard/statements/StatementPeriodHeader.vue'
import { useManualGivingDonations } from '@/composables/useManualGivingDonations'
import { useTaxPackExportModal } from '@/composables/useTaxPackExportModal'
import StatementTotalsCards from '@/components/dashboard/statements/StatementTotalsCards.vue'
import StatementDetailPanel from '@/components/dashboard/statements/StatementDetailPanel.vue'
import type { DonorFinancialYear } from '@/types/donorFinancialYear'
import type { UserStatement } from '@/types/userStatement'

const props = defineProps<{
  financialYear: DonorFinancialYear
  /** Present when Swiftaid issued a statement for this year; PDF still works at £0 without it. */
  issuedStatement?: UserStatement | null
  showTag?: boolean
  /** Full-width panel inside historic accordion (no nested card chrome). */
  embedded?: boolean
}>()

const router = useRouter()
const { manualDonations, loadManualDonations } = useManualGivingDonations()
const { openTaxPackExportModal } = useTaxPackExportModal()

const detailOpen = ref(false)

const manualForPeriod = computed(() =>
  donationsForTaxYearPeriod(
    manualDonations.value.filter(isDonorAddedDonation),
    props.financialYear.period
  )
)

const openDetail = () => {
  detailOpen.value = true
}

const closeDetail = () => {
  detailOpen.value = false
}

const downloadPdf = async () => {
  const period = props.financialYear.period
  try {
    await loadManualDonations()
    openTaxPackExportModal({
      period,
      taxYearTitle: periodKeyToTaxYearTitle(period),
      hasManualSection: manualForPeriod.value.length > 0,
    })
  } catch (error) {
    console.error('Failed to prepare PDF export', error)
  }
}

const contactSupport = () => {
  router.push({ name: 'Support' })
}
</script>

<template>
  <div
    class="sa-statement-card"
    :class="{ 'sa-statement-card--flat': embedded }"
  >
    <StatementPeriodHeader v-if="showTag" :period="financialYear.period" />
    <StatementTotalsCards :financial-year="financialYear" />

    <div class="sa-stmt-actions">
      <button
        v-if="!detailOpen"
        type="button"
        class="sa-btn-ghost"
        @click="openDetail"
      >
        {{ $t('Dashboard.Statements.ViewDetail') }}
      </button>
      <button v-else type="button" class="sa-btn-ghost" @click="closeDetail">
        {{ $t('Dashboard.Statements.HideDetail') }}
      </button>
      <button
        type="button"
        class="sa-btn-ghost"
        @click="downloadPdf"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fill-rule="evenodd"
            d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
            clip-rule="evenodd"
          />
        </svg>
        {{ $t('Dashboard.Statements.DownloadPdf') }}
      </button>
      <button type="button" class="sa-btn-ghost" @click="contactSupport">
        {{ $t('Dashboard.Statements.ContactSupport') }}
      </button>
    </div>

    <StatementDetailPanel
      v-if="detailOpen"
      :financial-year="financialYear"
      :manual-donations="manualForPeriod"
      @close="closeDetail"
    />
  </div>
</template>
